import {useState} from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
} from '@mui/material';

import CategorySelectInput from './CategorySelectInput';
import Input from '~/components/Input';
import Loading from '~/components/Loading';
import FetchError from '~/components/FetchError';
import SelectInput from '~/components/SelectInput';

import {getFieldsByCategoryKey} from './utils';
import {useCategoriesList} from '../hooks';
import {useConsultants} from '~/modules/frontdesk/hooks';
import {Consultant} from '$modules/booking/types';
import {getNameInitials} from '~/modules/booking/utils';

import {PRICE_COMPONENT_MODES} from './constants';
import {PrincingListFormikFormType} from '../PricingList';

import {consultantSelectionViewStyles} from '~/modules/booking/styles';

const {ADD_PRICE_MODE, EDIT_PRICE_MODE} = PRICE_COMPONENT_MODES;

interface IAddSingleNewPriceProps {
  formik: PrincingListFormikFormType;
  mode: typeof ADD_PRICE_MODE | typeof EDIT_PRICE_MODE;
}

interface IField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'radio';
  options?: {value: string | number; label: string}[];
}

const AddSingleNewPrice = ({formik, mode}: IAddSingleNewPriceProps) => {
  const isAddMode = mode === ADD_PRICE_MODE;
  const isEditMode = mode === EDIT_PRICE_MODE;

  const [selectedCategoryName, setSelectedCategoryName] = useState<string>(
    formik.values.categoryName || ''
  );

  const {values, errors, touched, handleChange, handleBlur, setFieldValue} =
    formik;

  const {data} = useCategoriesList();

  const {
    isLoading,
    consultants: consultantList,
    isError,
  } = useConsultants({
    withoutFee: true,
  });

  const handleConsultantChange = (value: string) => {
    setFieldValue('doctorId', Number(value));
  };

  const handleCategoryChange = (id: string) => {
    if (!id) {
      setSelectedCategoryName('');
      setFieldValue('categoryName', '');
      setFieldValue('categoryId', '');
      setFieldValue('ccsdCode', '');
      setFieldValue('description', '');
      setFieldValue('eyeOption', '');
      setFieldValue('price', '');
      setFieldValue('doctorId', '');
      setFieldValue('consultationFee', '');
      setFieldValue('followupFee', '');
      return;
    }

    const selectedCategory = data?.find(cat => cat.id === id);
    if (selectedCategory) {
      setSelectedCategoryName(selectedCategory.name);
      setFieldValue('categoryName', selectedCategory.name);
      setFieldValue('categoryId', id);
      setFieldValue('ccsdCode', '');
      setFieldValue('description', '');
      setFieldValue('eyeOption', '');
      setFieldValue('price', '');
      setFieldValue('doctorId', '');
      setFieldValue('consultationFee', '');
      setFieldValue('followupFee', '');
    }
  };

  const categoryName = selectedCategoryName;
  const fields = getFieldsByCategoryKey(categoryName || '');

  const renderConsultantMenuItem = (item: Consultant) => {
    return (
      <MenuItem
        key={item.id}
        value={item.id.toString()}
        sx={consultantSelectionViewStyles.menuItemContainer}
      >
        {item.profileUrl ? (
          <img
            src={item.profileUrl}
            style={consultantSelectionViewStyles.menuItemProfileImage}
          />
        ) : (
          <span style={consultantSelectionViewStyles.menuItemInitial}>
            {getNameInitials(item.fullName)}
          </span>
        )}
        <span>{item.fullName}</span>
      </MenuItem>
    );
  };

  const renderValue = (value: string) => {
    if (!consultantList?.length) return 'No consultant available';
    if (!value) return 'Select a consultant';
    const item = consultantList.find(it => it.id.toString() === value);
    if (!item) {
      handleConsultantChange('');
      return '';
    }

    return (
      <Box sx={consultantSelectionViewStyles.menuItemContainer}>
        {item.profileUrl ? (
          <img
            src={item.profileUrl}
            style={consultantSelectionViewStyles.menuItemProfileImage}
          />
        ) : (
          <span style={consultantSelectionViewStyles.menuItemInitial}>
            {getNameInitials(item.fullName)}
          </span>
        )}
        <span>{item.fullName}</span>
      </Box>
    );
  };

  const renderTextField = (
    field: IField,
    value: string,
    error: boolean,
    helperText: string | undefined
  ) => (
    <FormControl fullWidth key={field.name}>
      <div className="field-label">{field.label}</div>
      <Input
        name={field.name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        type={field.type}
        placeholder={`Enter ${field.label}`}
        error={error}
        touched={!!touched?.[field.name]}
        helperText={helperText}
      />
    </FormControl>
  );

  const renderSelectField = (field: IField) => {
    const error = touched?.[field.name] && Boolean(errors?.[field.name]);
    const helperText = touched?.[field.name] && errors?.[field.name];

    if (!consultantList?.length) {
      return (
        <div className="dialog-field-container">
          <div className="field-label">{field.label}</div>
          <div className="text-sm text-gray-500 mt-2">
            No consultants available
          </div>
        </div>
      );
    }

    return (
      <div className="dialog-field-container">
        <div className="field-label">{field.label}</div>
        <SelectInput
          fullWidth={true}
          name={field.name}
          placeholder={`Select a ${field.label}`}
          items={consultantList}
          renderValue={renderValue}
          renderItem={renderConsultantMenuItem}
          onChange={handleConsultantChange}
          value={formik.values.doctorId?.toString() ?? ''}
          error={error}
        />
        {error && <div className="text-sm text-red-500 mt-1">{helperText}</div>}
      </div>
    );
  };

  const renderField = (field: IField) => {
    if (
      isAddMode &&
      !consultantList?.length &&
      ['consultationFee', 'followupFee'].includes(field.name)
    ) {
      return null;
    }

    const value = values[field.name as keyof typeof values] || '';
    const error = touched?.[field.name] && Boolean(errors?.[field.name]);
    const helperText = touched?.[field.name] && errors?.[field.name];

    switch (field.type) {
      case 'select':
        return renderSelectField(field);
      case 'radio':
        return (
          <FormControl fullWidth key={field.name}>
            <div className="field-label">{field.label}</div>
            <RadioGroup
              name={field.name}
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {field.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {error && <div className="text-sm text-red-500">{helperText}</div>}
          </FormControl>
        );
      default:
        return renderTextField(field, value, error, helperText);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <FetchError />;
  }

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4 max-w-80">
      {isAddMode ? (
        <>
          <div className="field-label">Select a category</div>
          <CategorySelectInput
            name="categoryId"
            value={values.categoryId}
            onChange={handleCategoryChange}
            onBlur={handleBlur}
            error={touched?.categoryId && Boolean(errors?.categoryId)}
            placeholder="Choose one option"
          />
          {touched?.categoryId && errors?.categoryId && (
            <div className="text-sm text-red-500">{errors?.categoryId}</div>
          )}
        </>
      ) : null}

      {fields.map(field =>
        isEditMode &&
        (field.name === 'ccsdCode' || field.name === 'doctorId') ? null : (
          <div key={field.name}>{renderField(field)}</div>
        )
      )}
    </form>
  );
};

export default AddSingleNewPrice;
